// 3rd party imports
import * as React from 'react';
import { Link } from 'react-router-dom';
import { inject } from 'mobx-react';

// bernie
import { ContextStore } from 'bernie-core';

// mobx
import { Legal } from 'src/models';
import { LegalStore } from '../../stores';

// ui
import { UitkHeading, UitkSubheading, UitkText } from 'uitk-react-text';
import { UitkIcon } from 'uitk-react-icons';
import { UitkLink } from 'uitk-react-link';
import { UitkMoreInfoTrigger } from 'uitk-react-more-info-trigger';
import { UitkSpacing } from 'uitk-react-spacing';
import { modifyLocaleParam } from '../../util/LocaleParamTransformer';

export interface TermsOfUseProps {
  context?: ContextStore;
  legal?: LegalStore;
}

export interface TermsOfUseState {
  legal?: Legal;
}

@inject('context', 'legal')
export class TermsOfUse extends React.Component<TermsOfUseProps, TermsOfUseState> {
  constructor(props) {
    super(props);

    const siteId = this.props.context.site.id;
    this.state = {
      legal: this.props.legal.getConfig(siteId),
    };
  }

  public render() {
    const { address, email, privacyPolicyLink } = this.state.legal;

    return (
      <UitkSpacing className="container--small" padding={{ block: 'twelve' }}>
        <main>
          <UitkSubheading>Last updated 20th December 2019</UitkSubheading>
          <UitkHeading tag="h1" size={3}>
            Terms Of Use
          </UitkHeading>
          <UitkText spacing="six">
            These Terms of Use (“Terms”) are between you (“you”) and Expedia Group (“Expedia,” “we,” “our” or “us”) and
            controls your use of the Expedia Group Affiliate Program website, including but not limited to: (the
            “Site”).
          </UitkText>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Changes to these terms
              </UitkHeading>
              <UitkText spacing="two">
                We may revise or update these Terms from time to time. All changes are effective as soon as they are
                posted. Your continued use of the Site following the posting of revised Terms constitutes your
                acceptance and agreement to be bound by the revised Terms. These Terms are current as of the date listed
                at the top of the page.
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Collection and use of your information
              </UitkHeading>
              <UitkText spacing="two">
                All information we collect through or in connection with this Site is subject to our Privacy Statement
                and you consent to all actions taken by us with respect to your information in compliance with the{' '}
                <Link to={`/${modifyLocaleParam(this.props.context.locale)}${privacyPolicyLink}`}>Privacy Policy</Link>
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Intellectual property
              </UitkHeading>
              <UitkText spacing="two">
                All information, artwork, text, video, audio, media files and images displayed on or contained within
                the Site (collectively, the “Content”) are the property of Expedia or its third-party licensors.
                Expedia, or its third-party licensors, as applicable, retain all right, title and interest in and to the
                Content and all intellectual property rights related thereto. You do not have, nor does your use of the
                Site give you, any ownership interest in the Content or the Site.
              </UitkText>
              <UitkText spacing="four">
                The Site and the Content are protected by U.S. and/or international intellectual property and/or
                proprietary rights laws, which may include copyright, trademark, patent or trade secret rights. You may
                use the Site solely for your personal or internal business use. You may not modify, copy, distribute,
                transmit, display, perform, reproduce, publish, license, create derivative works from, transfer, or sell
                or re-sell the Site or any Content.
              </UitkText>
              <UitkText spacing="four">
                All brands, names, logos, product and service names, designs and slogans incorporated into the Site
                (collectively the “Expedia Marks”) are owned by Expedia, its affiliates or its licensors. You may not
                use the Expedia Marks without Expedia’s prior written permission.
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Links to third-party sites
              </UitkHeading>
              <UitkText spacing="two">
                The Site may provide links to third-party websites or other websites of Expedia’s affiliates or related
                companies (“Unrelated Sites”). These links are provided solely as a convenience to you, and these Terms
                do not control the Unrelated Sites. Your access, reliance on and use of these links and any Unrelated
                Sites is entirely at your own risk and is subject to such Unrelated Sites’ terms and conditions. Expedia
                is not responsible for, and will not have any liability or responsibility to you, for the content on, or
                your use of, any third-party website. Our inclusion of hyperlinks to Unrelated Sites does not imply any
                endorsement of such sites or the material found on them.
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Site standards
              </UitkHeading>
              <UitkText spacing="two">
                You agree to comply with all applicable federal, state, local and international laws and regulations
                when using the Site. Additionally, you may not, without our express written, prior permission: (a)
                access, monitor or copy any content or information on the Site using any robot, spider, scraper or other
                automated means or any manual process for any purpose; (b) violate the restrictions in any robot
                exclusion headers on Site or bypass or circumvent other measures employed to prevent or limit access to
                Site; (c) take any action that imposes, or may impose, in our discretion, an unreasonable or
                disproportionately large load on our infrastructure; (d) deep-link to any portion of Site for any
                purpose; or (e) “frame”, “mirror” or otherwise incorporate any part of the Site into any other website
                (each of the preceding, a “Prohibited Use”).
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Account security
              </UitkHeading>
              <UitkText spacing="two">
                As a condition of your use of the Site, You agree that (a) You are responsible for maintaining the
                confidentiality of Your account and password; (b) You may not use anyone else’s account at any time to
                access the Site; (c) You must notify the Site immediately if You detect any unauthorized use of Your
                account; and (d) You are responsible for all activities that occur using Your account.
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Response to violations
              </UitkHeading>
              <UitkText spacing="two">
                Expedia reserves the right to take any action we determine is necessary if we believe that your use of
                the Site violates these Terms, infringes the rights of any person or entity, threatens the personal
                safety of any person or entity, is a Prohibited Use, or could create liability for Expedia. Any such
                action by Expedia does not limit our rights or remedies at law or in equity.
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Disclaimer of warranties and limits on liability
              </UitkHeading>
              <UitkText spacing="two">
                THE SITE IS PROVIDED “AS IS” WITH ALL FAULTS AND DEFECTS AND WITHOUT WARRANTY OF ANY KIND, WHETHER
                EXPRESS OR IMPLIED INCLUDING, WITHOUT LIMITATION, ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR
                A PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT, AND WARRANTIES THAT MAY ARISE OUT OF COURSE OF
                DEALING, COURSE OF PERFORMANCE, USAGE OR TRADE PRACTICE. WITHOUT LIMITATION TO THE FOREGOING, EXPEDIA
                MAKES NO REPRESENTATION OF ANY KIND THAT THE SITE WILL MEET YOUR REQUIREMENTS, ACHIEVE ANY INTENDED
                RESULTS, BE COMPATIBLE OR WORK WITH ANY OTHER SOFTWARE, APPLICATIONS, SYSTEMS OR SITE, OPERATE WITHOUT
                INTERRUPTION, MEET ANY PERFORMANCE OR RELIABILITY STANDARDS OR BE ERROR FREE, THAT ANY ERRORS OR DEFECTS
                CAN OR WILL BE CORRECTED, THAT THE INFORMATION THEREIN WILL BE SUITABLE FOR YOUR NEEDS, OR THAT THE SITE
                IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. EXPEDIA MAY MAKE IMPROVEMENTS AND/OR CHANGES TO THE SITE
                AT ANY TIME. THE INCLUSION OF ANY PARTICULAR PRODUCT OR SERVICE ON THE SITE DOES NOT CONSTITUTE ANY
                ENDORSEMENT OR RECOMMENDATION OF SUCH PRODUCTS OR SERVICE BY EXPEDIA.
              </UitkText>
              <UitkText spacing="four">
                SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF OR LIMITATIONS ON IMPLIED WARRANTIES OR THE LIMITATIONS
                ON THE APPLICABLE STATUTORY RIGHTS OF A CONSUMER, SO SOME OR ALL OF THE ABOVE EXCLUSIONS AND LIMITATIONS
                MAY NOT APPLY TO YOU.
              </UitkText>
              <UitkText spacing="four">
                IN NO EVENT SHALL EXPEDIA BE LIABLE FOR ANY DIRECT, INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL OR
                CONSEQUENTIAL DAMAGES ARISING OUT OF, OR IN ANY WAY CONNECTED WITH, YOUR ACCESS TO, OR USE OF THE SITE
                OR WITH ANY DELAY OR INABILITY TO ACCESS, DISPLAY OR USE THE SITE, INCLUDING OUR INVESTIGATION OF YOUR
                USE OF THE SITE, WHETHER BASED ON A THEORY OF NEGLIGENCE, CONTRACT, TORT, STRICT LIABILITY, OR
                OTHERWISE, AND EVEN IF EXPEDIA HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </UitkText>
              <UitkText spacing="four">
                If, despite the limitation above, Expedia is found liable for any loss or damage which arises out of or
                is in any way connected with any of the occurrences described above, then the liability of Expedia will
                in no event exceed, in the aggregate, the greater of (a) the fees (if any) you paid to Expedia for
                access to the Site, or (b) One-Hundred Dollars (US$100.00).
              </UitkText>
              <UitkText spacing="four">
                The limitation of liability reflects the allocation of risk between the parties, and Expedia would be
                unwilling to make the Site available to you without such limitations. The limitations specified in this
                section will survive and apply even if any limited remedy specified in these terms is found to have
                failed its essential purpose. The limitations of liability provided in these terms inure to the benefit
                of Expedia.
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Indemnification
              </UitkHeading>
              <UitkText spacing="two">
                You agree to indemnify, defend and hold harmless Expedia, its affiliates and their officers, directors,
                employees, agents, affiliates, successors and assigns from and against any and all claims, causes of
                action, demands, recoveries, losses, damages, fines, penalties or other costs or expenses of any kind or
                nature including but not limited to reasonable legal and accounting fees resulting from your: (a) breach
                of these Terms; (b) violation of any law or the rights of a third party; or (c) use of the Site or any
                of the Contents.
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Feedback
              </UitkHeading>
              <UitkText spacing="two">
                We appreciate hearing from you. Please be aware that by submitting suggestions, comments or other
                feedback related to the Site or Content (collectively, “Submissions”), you hereby assign to Expedia all
                your worldwide right, title and interest in and to all Submissions, even if you have designated such
                Submissions as confidential. You agree to promptly provide assistance as may, in Expedia’s opinion, be
                necessary to document, perfect and maintain Expedia’s rights in the Submissions. Furthermore, you shall
                ensure that the Submissions shall not infringe any right of any third party. If you do not agree to
                these terms and conditions, please do not provide us with any Submissions.
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                General terms
              </UitkHeading>
              <UitkText spacing="two">
                These Terms are governed by the U.S. Federal Arbitration Act, federal arbitration law, and for
                reservations made by U.S. residents, the laws of the state in which your billing address is located,
                without regard to principles of conflicts of laws. Use of this Site is unauthorized in any jurisdiction
                that does not give effect to all provisions of these Terms, including, without limitation, this
                paragraph.
              </UitkText>
              <UitkText spacing="four">
                You agree that no joint venture, partnership, or employment relationship exists between you and Expedia
                Group Companies as a result of these Terms or use of this Site. Our performance of these Terms are
                subject to existing laws and legal process, and nothing contained in these Terms limits our right to
                comply with law enforcement or other governmental or legal requests or requirements relating to your use
                of this Site or information provided to or gathered by us with respect to such use.
              </UitkText>
              <UitkText spacing="four">
                If any part of these Terms is found to be invalid, illegal or unenforceable, the validity, legality and
                enforceability of the remaining provisions will not in any way be affected or impaired. Our failure or
                delay in enforcing any provision of these Terms at any time does not waive our right to enforce the same
                or any other provision(s) hereof in the future.
              </UitkText>
              <UitkText spacing="four">
                These Terms (and any other terms and conditions or policies referenced herein) constitutes the entire
                agreement between you and Expedia, with respect to this Site. A printed version of these Terms and of
                any notice given in electronic form shall be admissible in judicial, arbitration or any other
                administrative proceedings to the same extent and subject to the same conditions as other business
                documents and records originally generated and maintained in printed form. Fictitious names of
                companies, products, people, characters, and/or data mentioned on this Site are not intended to
                represent any real individual, company, product, or event.
              </UitkText>
              <UitkText spacing="four">
                You acknowledge that irreparable harm would result in the event of breach by you of these Terms and that
                monetary damages in such event could be inadequate to compensate Expedia. Consequently, in the event of
                breach of any of these Terms, Expedia is entitled, in addition to such monetary relief as may be
                recoverable, to such temporary, preliminary and/or permanent injunctive relief as may be necessary to
                restrain any continuing or further breach of these Terms, without showing or proving any actual damages
                or posting a bond or comparable security. Any rights not expressly granted herein are reserved.
              </UitkText>
            </div>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                Notices, questions or concerns.
              </UitkHeading>
              <UitkText spacing="two">All notices should be sent to: {address}</UitkText>
              <UitkText spacing="four">
                All other comments, requests for technical support and other communications relating to the Site should
                be directed to:{' '}
                <UitkLink inline={true}>
                  <a href={`mailto:${email}`}>{email}</a>
                </UitkLink>
              </UitkText>
            </div>
          </UitkSpacing>
        </main>
      </UitkSpacing>
    );
  }
}
